/* Copyright (c) Trimble Inc. All rights reserved. */

import { create } from 'zustand';
import { UserTable } from '../tables/User.table';
import { type User, type UserProfile } from '../models/User.model';
import { Logger } from '@tcp-base/toolkit';

export interface UserStoreState {
  userProfile: UserProfile;
  saveUserData: (userData: User) => Promise<void>;
  getUserData: () => Promise<UserProfile>;
  removeUserData: () => Promise<void>;
}

export const useUserStore = create<UserStoreState>((set, get) => ({
  userProfile: {},

  saveUserData: async (userData: User): Promise<void> => {
    try {
      // Save to IndexedDB using UserTable singleton
      await UserTable.saveUserData(userData);

      // Get updated user profile with calculated fields
      const updatedProfile = await UserTable.getUserData();

      // Update zustand state
      set({ userProfile: updatedProfile });
    } catch (error) {
      Logger.error('Failed to save user data:', error);
      throw error;
    }
  },

  getUserData: async (): Promise<UserProfile> => {
    const { userProfile } = get();

    // Check if zustand has non-empty user profile (at least email or given_name)
    if (userProfile.email || userProfile.given_name) {
      return userProfile;
    }

    try {
      // If zustand is empty, check IndexedDB via UserTable
      const dbUserProfile = await UserTable.getUserData();
      // If IndexedDB has user data, store it in zustand state
      if (dbUserProfile.email || dbUserProfile.given_name) {
        set({ userProfile: dbUserProfile });
        return dbUserProfile;
      }

      // Return empty if no data found anywhere
      return userProfile;
    } catch (error) {
      Logger.error('Failed to get user data from IndexedDB:', error);
      return userProfile;
    }
  },

  removeUserData: async (): Promise<void> => {
    try {
      // Clear zustand state immediately
      set({ userProfile: {} });

      // Remove from IndexedDB using UserTable singleton
      await UserTable.removeUserData();
    } catch (error) {
      Logger.error('Failed to remove user data:', error);
      throw error;
    }
  },
}));

/**
 * Helper function to get user profile from UserStore (synchronous)
 * Note: This only returns from zustand state. Use getUserData() for IndexedDB fallback.
 */
export const getUserProfile = (): UserProfile => {
  return useUserStore.getState().userProfile;
};

// Initialize user data on first import (commented out for testing)
// useUserStore.getState().getUserData();
